import { TunaDialog, DialogContent, DialogActions } from '../primitives/dialog.js'
import { Button } from '../primitives/button.js'
import type { FC, MouseEvent } from 'react'
import React, { useId } from 'react'
import { DialogTitle } from './dialog-title.js'
import moduleStyles from './dialog.module.css'

export interface CDialogProps {
  className?: string
  open: boolean
  controls?: React.ReactNode
  showClose?: boolean
  closeText?: string
  title?: string
  /** The title's id, for content the title also names (a radio group asking the title's question) */
  titleId?: string
  showOK?: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  fullWidth?: boolean
  fullScreen?: boolean
  children?: React.ReactNode
  style?: React.CSSProperties
  onClick?: (ev: MouseEvent) => void
  /** The element that takes focus on open, instead of the first tabbable one (see `TunaDialog`) */
  initialFocus?: React.RefObject<HTMLElement | null>
  onClose(ev?: MouseEvent): void
  onOK?(ev: MouseEvent): void
}

export const Dialog: FC<CDialogProps> = ({
  className,
  children,
  open,
  controls = null,
  onClose,
  closeText = 'Close',
  title,
  titleId: givenTitleId,
  showClose = false,
  showOK = false,
  onOK,
  maxWidth,
  fullWidth,
  fullScreen,
  style,
  onClick,
  initialFocus,
}) => {
  const generatedTitleId = useId()
  const titleId = givenTitleId ?? generatedTitleId
  if (!open) {
    return null
  }

  const showActions = showOK || showClose

  return (
    <TunaDialog
      open={open}
      className={className}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      style={style}
      aria-labelledby={title ? titleId : undefined}
      {...(initialFocus ? { initialFocus } : {})}
    >
      {title ? (
        <DialogTitle onClose={onClose} id={titleId} controls={controls}>
          {title}
        </DialogTitle>
      ) : null}
      <DialogContent className={moduleStyles.dialogContent} onClick={onClick}>
        {children}
      </DialogContent>
      {showActions ? (
        <DialogActions>
          {showOK && <Button onClick={onOK}>OK</Button>}
          {showClose && <Button onClick={onClose}>{closeText}</Button>}
        </DialogActions>
      ) : null}
    </TunaDialog>
  )
}

export default Dialog
